import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { notificationsApi } from '../../services/api'

const initialState = {
  notifications: [],
  unreadCount: 0,
  isLoading: false,
  error: null,
}

// Async thunks
export const fetchNotifications = createAsyncThunk(
  'notifications/fetchNotifications',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await notificationsApi.getAll(params)
      
      if (response.success) {
        return response.data
      } else {
        throw new Error(response.error?.message || 'Ошибка загрузки уведомлений')
      }
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

export const markNotificationAsRead = createAsyncThunk(
  'notifications/markAsRead',
  async (id, { dispatch, rejectWithValue }) => {
    try {
      const response = await notificationsApi.markAsRead(id)
      
      if (response.success) {
        dispatch(fetchNotifications({ limit: 10 }))
        return id
      } else {
        throw new Error(response.error?.message || 'Ошибка отметки уведомления')
      }
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

export const markAllNotificationsAsRead = createAsyncThunk(
  'notifications/markAllAsRead',
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const response = await notificationsApi.markAllAsRead()
      
      if (response.success) {
        dispatch(fetchNotifications({ limit: 10 }))
        return true
      } else {
        throw new Error(response.error?.message || 'Ошибка отметки уведомлений')
      }
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

export const deleteNotification = createAsyncThunk(
  'notifications/delete',
  async (id, { dispatch, rejectWithValue }) => {
    try {
      const response = await notificationsApi.delete(id)
      
      if (response.success) {
        dispatch(fetchNotifications({ limit: 10 }))
        return id
      } else {
        throw new Error(response.error?.message || 'Ошибка удаления уведомления')
      }
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

const notificationSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null
    },
    
    addNotification: (state, action) => {
      state.notifications.unshift(action.payload)
      if (!action.payload.is_read) {
        state.unreadCount += 1
      }
    },
  },
  extraReducers: (builder) => {
    // Fetch Notifications
    builder
      .addCase(fetchNotifications.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchNotifications.fulfilled, (state, action) => {
        state.isLoading = false
        state.notifications = action.payload.notifications || []
        state.unreadCount = action.payload.unread_count || 0
        state.error = null
      })
      .addCase(fetchNotifications.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload
      })
    
    // Mark as Read
    builder
      .addCase(markNotificationAsRead.fulfilled, (state, action) => {
        const notification = state.notifications.find(n => n.id === action.payload)
        if (notification && !notification.is_read) {
          notification.is_read = true
          state.unreadCount = Math.max(0, state.unreadCount - 1)
        }
      })
    
    // Mark All as Read
    builder
      .addCase(markAllNotificationsAsRead.fulfilled, (state) => {
        state.notifications.forEach(notification => {
          notification.is_read = true
        })
        state.unreadCount = 0
      })
    
    // Delete Notification
    builder
      .addCase(deleteNotification.fulfilled, (state, action) => {
        const index = state.notifications.findIndex(n => n.id === action.payload)
        if (index !== -1) {
          const notification = state.notifications[index]
          if (!notification.is_read) {
            state.unreadCount = Math.max(0, state.unreadCount - 1)
          }
          state.notifications.splice(index, 1)
        }
      })
  },
})

export const { clearError, addNotification } = notificationSlice.actions

// Selectors
export const selectUnreadNotifications = (state) => {
  return state.notifications.notifications.filter(n => !n.is_read)
}

export default notificationSlice.reducer